/**
 * Meta Connection Settings — Facebook & Instagram extended permissions
 *
 * Connect Facebook/Instagram with extended scopes (pages, messenger, ads).
 * Manage connected pages, token status, and disconnect flow.
 * Real apiFetch. No mocks.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { authClient } from "@/lib/auth-client";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Unplug,
  RefreshCw,
  Shield,
  ExternalLink,
} from "lucide-react";

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <circle cx="12" cy="12" r="11" fill="#1877F2" />
      <path
        d="M16.67 15.47l.55-3.47H13.9v-2.25c0-.95.47-1.87 1.95-1.87h1.51V5.01s-1.37-.23-2.68-.23c-2.74 0-4.53 1.66-4.53 4.67V12H7.08v3.47h3.07V23.8a12.2 12.2 0 003.77 0V15.47h2.75z"
        fill="#fff"
      />
    </svg>
  );
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className}>
      <defs>
        <radialGradient id="ig-settings-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect x="1" y="1" width="22" height="22" rx="6" fill="url(#ig-settings-grad)" />
      <circle cx="12" cy="12" r="4.5" fill="none" stroke="#fff" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="#fff" />
      <rect x="1" y="1" width="22" height="22" rx="6" fill="none" stroke="#fff" strokeWidth="1.8" />
    </svg>
  );
}

interface ConnectedPage {
  id: string;
  name: string;
  category: string;
  isSelected: boolean;
  platform: "facebook" | "instagram";
}

interface TokenStatus {
  isConnected: boolean;
  expiresAt: string | null;
  status: "valid" | "expiring" | "expired" | "disconnected";
  scopes: string[];
}

interface MetaConnection {
  facebook: TokenStatus;
  instagram: TokenStatus;
  pages: ConnectedPage[];
}

const INITIAL_STATUS: TokenStatus = {
  isConnected: false,
  expiresAt: null,
  status: "disconnected",
  scopes: [],
};

const INITIAL_CONNECTION: MetaConnection = {
  facebook: INITIAL_STATUS,
  instagram: INITIAL_STATUS,
  pages: [],
};

const FACEBOOK_SCOPES = [
  "pages_manage_posts",
  "pages_read_engagement",
  "pages_messaging",
  "ads_management",
  "ads_read",
  "business_management",
  "leads_retrieval",
  "pages_manage_metadata",
] as const;

const STATUS_CONFIG = {
  valid: {
    icon: CheckCircle2,
    label: "Connected",
    className: "text-[var(--color-success)]",
    bgClassName: "bg-[var(--color-success)]/10",
  },
  expiring: {
    icon: AlertTriangle,
    label: "Expiring Soon",
    className: "text-[var(--color-warning)]",
    bgClassName: "bg-[var(--color-warning)]/10",
  },
  expired: {
    icon: XCircle,
    label: "Expired",
    className: "text-[var(--color-destructive)]",
    bgClassName: "bg-[var(--color-destructive)]/10",
  },
  disconnected: {
    icon: Unplug,
    label: "Not Connected",
    className: "text-[var(--color-muted-foreground)]",
    bgClassName: "bg-[var(--color-muted)]/10",
  },
} as const;

function TokenStatusBadge({ status }: { status: TokenStatus["status"] }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bgClassName} ${config.className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {config.label}
    </span>
  );
}

export default function MetaConnectPage() {
  const [connection, setConnection] = useState<MetaConnection>(INITIAL_CONNECTION);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isConnecting, setIsConnecting] = useState<"facebook" | "instagram" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState<"facebook" | "instagram" | null>(null);

  const fetchConnection = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: MetaConnection }>("/integrations/meta/status");
      setConnection(res.data);
    } catch {
      setConnection(INITIAL_CONNECTION);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnection();
  }, [fetchConnection]);

  const handleConnectFacebook = async () => {
    setIsConnecting("facebook");
    setError(null);
    try {
      await authClient.signIn.social({
        provider: "facebook",
        callbackURL: "/settings/integrations/meta-connect",
        scopes: [...FACEBOOK_SCOPES],
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect Facebook. Please try again.");
      setIsConnecting(null);
    }
  };

  const handleConnectInstagram = async () => {
    setIsConnecting("instagram");
    setError(null);
    try {
      await authClient.signIn.social({
        provider: "instagram",
        callbackURL: "/settings/integrations/meta-connect",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect Instagram. Please try again.");
      setIsConnecting(null);
    }
  };

  const handleDisconnect = async (platform: "facebook" | "instagram") => {
    setShowDisconnectConfirm(null);
    setError(null);
    try {
      await apiFetch(`/integrations/meta/${platform}/disconnect`, { method: "POST" });
      setSuccess(`${platform === "facebook" ? "Facebook" : "Instagram"} disconnected successfully.`);
      await fetchConnection();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to disconnect ${platform}.`);
    }
  };

  const handleTogglePage = async (pageId: string, isSelected: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      await apiFetch(`/integrations/meta/pages/${pageId}`, {
        method: "PATCH",
        body: JSON.stringify({ isSelected }),
      });
      setConnection((prev) => ({
        ...prev,
        pages: prev.pages.map((p) => (p.id === pageId ? { ...p, isSelected } : p)),
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update page selection.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefreshToken = async (platform: "facebook" | "instagram") => {
    setError(null);
    try {
      await apiFetch(`/integrations/meta/${platform}/refresh`, { method: "POST" });
      setSuccess("Token refreshed successfully.");
      await fetchConnection();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh token.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-[var(--color-muted-foreground)]" />
      </div>
    );
  }

  const facebookPages = connection.pages.filter((p) => p.platform === "facebook");
  const instagramPages = connection.pages.filter((p) => p.platform === "instagram");

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Meta Connections</h1>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
          Connect Facebook and Instagram to manage pages, messenger, and ads from one place.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
          <XCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 px-4 py-3 text-sm text-[var(--color-success)]">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {success}
        </div>
      )}

      {/* Facebook Connection */}
      <PlatformCard
        platform="facebook"
        icon={<FacebookIcon className="h-6 w-6" />}
        label="Facebook"
        description="Manage pages, respond to Messenger, run ads, and capture leads."
        tokenStatus={connection.facebook}
        pages={facebookPages}
        isConnecting={isConnecting === "facebook"}
        isSaving={isSaving}
        showDisconnectConfirm={showDisconnectConfirm === "facebook"}
        onConnect={handleConnectFacebook}
        onDisconnect={() => handleDisconnect("facebook")}
        onDisconnectConfirm={() => setShowDisconnectConfirm("facebook")}
        onDisconnectCancel={() => setShowDisconnectConfirm(null)}
        onRefreshToken={() => handleRefreshToken("facebook")}
        onTogglePage={handleTogglePage}
      />

      {/* Instagram Connection */}
      <PlatformCard
        platform="instagram"
        icon={<InstagramIcon className="h-6 w-6" />}
        label="Instagram"
        description="Manage your Instagram business profile and respond to DMs."
        tokenStatus={connection.instagram}
        pages={instagramPages}
        isConnecting={isConnecting === "instagram"}
        isSaving={isSaving}
        showDisconnectConfirm={showDisconnectConfirm === "instagram"}
        onConnect={handleConnectInstagram}
        onDisconnect={() => handleDisconnect("instagram")}
        onDisconnectConfirm={() => setShowDisconnectConfirm("instagram")}
        onDisconnectCancel={() => setShowDisconnectConfirm(null)}
        onRefreshToken={() => handleRefreshToken("instagram")}
        onTogglePage={handleTogglePage}
      />

      {/* Permissions Info */}
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 shrink-0 text-[var(--color-muted-foreground)]" />
          <div>
            <p className="text-sm font-medium text-[var(--color-foreground)]">About Permissions</p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              We request extended permissions to manage your pages, respond to messages, and run ads on your
              behalf. You can revoke access at any time from your Meta account settings or by disconnecting here.
            </p>
            <a
              href="https://www.facebook.com/settings/?tab=business_tools"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
            >
              Manage in Meta Business Settings
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PlatformCardProps {
  platform: "facebook" | "instagram";
  icon: React.ReactNode;
  label: string;
  description: string;
  tokenStatus: TokenStatus;
  pages: ConnectedPage[];
  isConnecting: boolean;
  isSaving: boolean;
  showDisconnectConfirm: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onDisconnectConfirm: () => void;
  onDisconnectCancel: () => void;
  onRefreshToken: () => void;
  onTogglePage: (pageId: string, isSelected: boolean) => void;
}

function PlatformCard({
  platform,
  icon,
  label,
  description,
  tokenStatus,
  pages,
  isConnecting,
  isSaving,
  showDisconnectConfirm,
  onConnect,
  onDisconnect,
  onDisconnectConfirm,
  onDisconnectCancel,
  onRefreshToken,
  onTogglePage,
}: PlatformCardProps) {
  const isConnected = tokenStatus.isConnected;
  const formattedExpiry = tokenStatus.expiresAt
    ? new Date(tokenStatus.expiresAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="text-[var(--color-foreground)]">{icon}</div>
          <div>
            <h2 className="text-base font-medium text-[var(--color-foreground)]">{label}</h2>
            <p className="text-xs text-[var(--color-muted-foreground)]">{description}</p>
          </div>
        </div>
        <TokenStatusBadge status={tokenStatus.status} />
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        {!isConnected ? (
          <button
            type="button"
            onClick={onConnect}
            disabled={isConnecting}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] shadow-sm transition-colors hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
          >
            {isConnecting ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>Connect {label}</>
            )}
          </button>
        ) : (
          <div className="space-y-4">
            {/* Token Info */}
            <div className="flex items-center justify-between rounded-md bg-[var(--color-surface)] px-3 py-2">
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {formattedExpiry ? (
                  <>Token expires: <span className="font-medium text-[var(--color-foreground)]">{formattedExpiry}</span></>
                ) : (
                  "Token expiry unknown"
                )}
              </div>
              <button
                type="button"
                onClick={onRefreshToken}
                className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
              >
                <RefreshCw className="h-3 w-3" />
                Refresh
              </button>
            </div>

            {/* Granted Scopes */}
            {tokenStatus.scopes.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-[var(--color-muted-foreground)]">Granted Permissions</p>
                <div className="flex flex-wrap gap-1.5">
                  {tokenStatus.scopes.map((scope) => (
                    <span
                      key={scope}
                      className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]"
                    >
                      {scope.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Connected Pages */}
            {pages.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-[var(--color-muted-foreground)]">
                  Pages ({pages.filter((p) => p.isSelected).length} of {pages.length} selected)
                </p>
                <div className="space-y-2">
                  {pages.map((page) => (
                    <label
                      key={page.id}
                      className="flex cursor-pointer items-center gap-3 rounded-md border border-[var(--color-border)] px-3 py-2 transition-colors hover:bg-[var(--color-surface)]"
                    >
                      <input
                        type="checkbox"
                        checked={page.isSelected}
                        disabled={isSaving}
                        onChange={(e) => onTogglePage(page.id, e.target.checked)}
                        className="h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-primary)]"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[var(--color-foreground)]">{page.name}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)]">{page.category}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Disconnect */}
            <div className="border-t border-[var(--color-border)] pt-4">
              {showDisconnectConfirm ? (
                <div className="flex items-center gap-3">
                  <p className="text-sm text-[var(--color-destructive)]">
                    Disconnect {label}? This will revoke all permissions.
                  </p>
                  <button
                    type="button"
                    onClick={onDisconnect}
                    className="rounded-md bg-[var(--color-destructive)] px-3 py-1.5 text-xs font-medium text-[var(--color-destructive-foreground)] transition-colors hover:bg-[var(--color-destructive)]/90"
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    onClick={onDisconnectCancel}
                    className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-xs font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-surface)]"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={onDisconnectConfirm}
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--color-destructive)] hover:underline"
                >
                  <Unplug className="h-3.5 w-3.5" />
                  Disconnect {label}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
